import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';

interface HeroStat {
  value: string;
  label: string;
}

interface HeroStatsEditorProps {
  eventId: string;
  eventType: 'retreat' | 'workation' | 'academy' | string;
  currentStats?: HeroStat[];
}

export function HeroStatsEditor({ eventId, eventType, currentStats = [] }: HeroStatsEditorProps) {
  const [stats, setStats] = useState<HeroStat[]>(
    currentStats.length > 0 ? currentStats : [
      { value: '', label: '' },
      { value: '', label: '' },
      { value: '', label: '' },
      { value: '', label: '' }
    ]
  );
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const getTableName = (type: string) => {
    const typeMap: Record<string, string> = {
      'retreat': 'retreats',
      'workation': 'workations',
      'academy': 'academies'
    };
    return typeMap[type] || 'retreats';
  };

  const handleStatChange = (index: number, field: 'value' | 'label', newValue: string) => {
    const newStats = [...stats];
    newStats[index] = { ...newStats[index], [field]: newValue };
    setStats(newStats);
  };

  const addStat = () => {
    setStats([...stats, { value: '', label: '' }]);
  };

  const removeStat = (index: number) => {
    if (stats.length > 1) {
      const newStats = stats.filter((_, i) => i !== index);
      setStats(newStats);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const tableName = getTableName(eventType);
      const { error } = await supabase
        .from(tableName as any)
        .update({ hero_stats: stats as any })
        .eq('id', eventId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Hero stats updated successfully"
      });
    } catch (error) {
      console.error('Error saving hero stats:', error);
      toast({
        title: "Error",
        description: "Failed to save hero stats",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hero Stats</CardTitle>
        <CardDescription>
          Customize the statistics shown in the hero section of your event page
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {stats.map((stat, index) => (
          <div key={index} className="flex gap-2 items-end">
            <div className="flex-1">
              <Label htmlFor={`value-${index}`}>Value</Label>
              <Input
                id={`value-${index}`}
                value={stat.value}
                onChange={(e) => handleStatChange(index, 'value', e.target.value)}
                placeholder="e.g. 8, 47, Epic"
              />
            </div>
            <div className="flex-1">
              <Label htmlFor={`label-${index}`}>Label</Label>
              <Input
                id={`label-${index}`}
                value={stat.label}
                onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                placeholder="e.g. Days, Participants"
              />
            </div>
            {stats.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeStat(index)}
                className="flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={addStat}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Stat
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={isSaving}
            size="sm"
            className="gap-2 ml-auto"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Changes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
